import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getDomain } from '../utils/authStorage';

const ModuleContext = createContext();

const PROJECT_KEY = 'constructionActiveProject';
const SURVEY_KEY = 'constructionActiveSurvey';

function readStoredProject() {
  try {
    const raw = localStorage.getItem(PROJECT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readStoredSurvey() {
  try {
    const raw = localStorage.getItem(SURVEY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Migrate legacy string date labels
    if (typeof parsed === 'string') {
      return { id: null, dateLabel: parsed, surveyDate: null };
    }
    return parsed;
  } catch {
    const legacy = localStorage.getItem(SURVEY_KEY);
    return legacy ? { id: null, dateLabel: legacy, surveyDate: null } : null;
  }
}

export function ModuleProvider({ children }) {
  const location = useLocation();
  const userDomain = getDomain();

  const determineModule = useCallback(() => {
    const domain = getDomain();
    if (location.pathname.startsWith('/construction') || domain === 'construction') {
      return 'construction';
    }
    return 'surveillance';
  }, [location.pathname]);

  const [activeModule, setActiveModule] = useState(determineModule);
  const [activeProject, setActiveProjectState] = useState(readStoredProject);
  const [activeSurvey, setActiveSurveyState] = useState(readStoredSurvey);

  // 1 user = 1 domain -> no switching permitted
  const canSwitchModules = false;

  useEffect(() => {
    setActiveModule(determineModule());
  }, [determineModule]);

  const setActiveSurvey = (survey) => {
    if (!survey) {
      setActiveSurveyState(null);
      localStorage.removeItem(SURVEY_KEY);
      return;
    }
    const normalized =
      typeof survey === 'string'
        ? { id: null, dateLabel: survey, surveyDate: null }
        : survey;
    setActiveSurveyState(normalized);
    localStorage.setItem(SURVEY_KEY, JSON.stringify(normalized));
  };

  const selectProject = (proj) => {
    if (!proj) {
      setActiveProjectState(null);
      localStorage.removeItem(PROJECT_KEY);
      setActiveSurvey(null);
      return;
    }
    setActiveProjectState(proj);
    localStorage.setItem(PROJECT_KEY, JSON.stringify(proj));
    // Survey list is loaded by ConstructionLayout; clear until latest is applied
    setActiveSurvey(null);
  };

  const switchModule = (moduleKey) => {
    if (moduleKey !== getDomain()) return; // guard: cannot switch outside own domain
    setActiveModule(moduleKey);
  };

  const surveyDateLabel =
    (activeSurvey && (activeSurvey.dateLabel || activeSurvey.surveyDate)) ||
    activeProject?.lastSurvey ||
    '';

  return (
    <ModuleContext.Provider
      value={{
        activeModule,
        switchModule,
        canSwitchModules,
        userDomain,
        activeProject,
        setActiveProject: selectProject,
        activeSurvey,
        setActiveSurvey,
        surveyDateLabel,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
}

export function useModule() {
  const context = useContext(ModuleContext);
  if (!context) {
    return {
      activeModule: 'surveillance',
      switchModule: () => {},
      canSwitchModules: false,
      userDomain: '',
      activeProject: null,
      setActiveProject: () => {},
      activeSurvey: null,
      setActiveSurvey: () => {},
      surveyDateLabel: '',
    };
  }
  return context;
}
